/**
 * MembershipsListPage — /admin/memberships
 *
 * Lists all memberships for the tenant with filters, a create button, and
 * bulk skip-next action.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import toast from 'react-hot-toast';

import { PageContainer } from '@/components/layout';
import { Button, Badge, Card, Dropdown, Modal } from '@/components/common';
import { useMemberships, useSkipNextMembership } from '@features/memberships/api/hooks';
import { MembershipFormModal } from './MembershipFormModal';
import { formatPaise } from '@features/catalog/config/ui';
import type { MembershipStatus, Membership, BillingCycle } from '@features/memberships/api/client';

const BILLING_CYCLE_OPTIONS: { value: BillingCycle | ''; label: string }[] = [
  { value: '', label: 'All cycles' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'half_yearly', label: 'Half-Yearly' },
  { value: 'yearly', label: 'Yearly' },
];

function currentMonthValue(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

const FiltersRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  flex-wrap: wrap;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  background: ${({ theme }) => theme.colors.surface};
  box-shadow: ${({ theme }) => theme.shadows.sm};
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  padding: ${({ theme }) => theme.spacing.md};
  text-align: left;
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  white-space: nowrap;
`;

const Td = styled.td`
  padding: ${({ theme }) => theme.spacing.md};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  color: ${({ theme }) => theme.colors.text};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const Tr = styled.tr<{ $selected?: boolean }>`
  background: ${({ $selected, theme }) =>
    $selected ? theme.colors.primaryLight : 'transparent'};
  cursor: pointer;
  transition: background ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.backgroundTertiary};
  }

  &:last-child td {
    border-bottom: none;
  }
`;

const CheckboxCell = styled(Td)`
  width: 40px;
`;

const BulkBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.primaryLight};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Center = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xl};
  color: ${({ theme }) => theme.colors.textSecondary};
  gap: ${({ theme }) => theme.spacing.md};
`;

function statusBadgeVariant(
  status: MembershipStatus,
): 'success' | 'warning' | 'error' | 'default' {
  switch (status) {
    case 'active':    return 'success';
    case 'paused':    return 'warning';
    case 'cancelled': return 'error';
    default:          return 'default';
  }
}

export const MembershipsListPage: React.FC = () => {
  const navigate = useNavigate();

  const [statusFilter, setStatusFilter] = useState<MembershipStatus | ''>('');
  const [cycleFilter, setCycleFilter] = useState<BillingCycle | ''>('');
  const [renewalMonthFilter, setRenewalMonthFilter] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  const { data, isLoading, isError, refetch } = useMemberships({
    ...(statusFilter ? { status: statusFilter } : {}),
    ...(cycleFilter ? { billing_cycle: cycleFilter } : {}),
    ...(renewalMonthFilter ? { renewal_month: renewalMonthFilter } : {}),
  });
  const skipNext = useSkipNextMembership();

  const memberships = data?.items ?? [];

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === memberships.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(memberships.map((m) => m.id));
    }
  };

  const handleBulkSkip = async () => {
    setBulkConfirmOpen(false);
    let failed = 0;
    for (const id of selectedIds) {
      try {
        await skipNext.mutateAsync(id);
      } catch {
        failed++;
      }
    }
    if (failed > 0) {
      toast.error(`${failed} skip(s) failed`);
    } else {
      toast.success(`Skipped next renewal for ${selectedIds.length} membership(s)`);
    }
    setSelectedIds([]);
  };

  return (
    <PageContainer
      title="Memberships"
      subtitle="Recurring billing subscriptions"
      data-testid="candeza-memberships-page"
      data-flow="memberships-page"
      actions={
        <Button
          data-testid="candeza-memberships-new-btn"
          data-flow="membership-new-btn"
          onClick={() => setFormOpen(true)}
        >
          + Create membership
        </Button>
      }
    >
      <FiltersRow>
        <Dropdown
          label=""
          placeholder="All statuses"
          testId="candeza-memberships-status-filter"
          value={statusFilter}
          onChange={(v) => setStatusFilter(v as MembershipStatus | '')}
          options={[
            { value: '', label: 'All statuses' },
            { value: 'active', label: 'Active' },
            { value: 'paused', label: 'Paused' },
            { value: 'cancelled', label: 'Cancelled' },
          ]}
        />

        <Dropdown
          label=""
          placeholder="All cycles"
          testId="candeza-memberships-cycle-filter"
          value={cycleFilter}
          onChange={(v) => setCycleFilter(v as BillingCycle | '')}
          options={BILLING_CYCLE_OPTIONS}
        />

        <input
          type="month"
          value={renewalMonthFilter}
          onChange={(e) => setRenewalMonthFilter(e.target.value)}
          placeholder="Renewal month"
          data-testid="candeza-memberships-renewal-month-filter"
          style={{ padding: '8px 10px', borderRadius: 6, border: '1px solid rgba(148,163,184,0.4)' }}
        />
        {renewalMonthFilter && (
          <Button size="sm" variant="ghost" onClick={() => setRenewalMonthFilter('')}>
            Clear month
          </Button>
        )}
        {!renewalMonthFilter && (
          <Button size="sm" variant="outline" onClick={() => setRenewalMonthFilter(currentMonthValue())}>
            This month
          </Button>
        )}
      </FiltersRow>

      {selectedIds.length > 0 && (
        <BulkBar>
          <span style={{ fontSize: 14 }}>{selectedIds.length} selected</span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setBulkConfirmOpen(true)}
          >
            Skip next renewal
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])}>
            Clear
          </Button>
        </BulkBar>
      )}

      {isLoading && (
        <Center data-testid="candeza-memberships-loading">
          Loading memberships…
        </Center>
      )}

      {isError && !isLoading && (
        <Center data-testid="candeza-memberships-error">
          <span>Failed to load memberships.</span>
          <Button size="sm" variant="outline" onClick={() => refetch()}>
            Retry
          </Button>
        </Center>
      )}

      {!isLoading && !isError && memberships.length === 0 && (
        <Center data-testid="candeza-memberships-empty">
          <span>No memberships found.</span>
          <Button size="sm" onClick={() => setFormOpen(true)}>
            Create first membership
          </Button>
        </Center>
      )}

      {!isLoading && !isError && memberships.length > 0 && (
        <Card padding="none">
          <TableWrapper data-testid="candeza-memberships-table">
            <StyledTable>
              <thead>
                <tr>
                  <Th>
                    <input
                      type="checkbox"
                      checked={
                        selectedIds.length === memberships.length &&
                        memberships.length > 0
                      }
                      onChange={toggleSelectAll}
                      aria-label="Select all"
                    />
                  </Th>
                  <Th>Student</Th>
                  <Th>Package</Th>
                  <Th>Status</Th>
                  <Th>Next Renewal</Th>
                  <Th>Amount</Th>
                </tr>
              </thead>
              <tbody>
                {memberships.map((m: Membership) => (
                  <Tr
                    key={m.id}
                    data-testid={`candeza-memberships-row-${m.id}`}
                    data-flow="membership-row"
                    $selected={selectedIds.includes(m.id)}
                    onClick={() => navigate(`/admin/memberships/${m.id}`)}
                  >
                    <CheckboxCell
                      onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(m.id)}
                        onChange={() => toggleSelect(m.id)}
                        aria-label={`Select ${m.student_name_snapshot}`}
                      />
                    </CheckboxCell>
                    <Td>{m.student_name_snapshot}</Td>
                    <Td>{m.package_name_snapshot}</Td>
                    <Td>
                      <Badge variant={statusBadgeVariant(m.status)}>
                        {m.status}
                      </Badge>
                    </Td>
                    <Td>{m.next_invoice_date}</Td>
                    <Td>{formatPaise(m.amount)}</Td>
                  </Tr>
                ))}
              </tbody>
            </StyledTable>
          </TableWrapper>
        </Card>
      )}

      {/* Bulk skip confirm */}
      <Modal
        isOpen={bulkConfirmOpen}
        onClose={() => setBulkConfirmOpen(false)}
        title="Skip next renewal?"
        size="sm"
      >
        <p style={{ marginBottom: 16 }}>
          This will skip the next renewal for {selectedIds.length} membership(s).
          Invoices will not be generated for the upcoming cycle.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="outline" onClick={() => setBulkConfirmOpen(false)}>
            Cancel
          </Button>
          <Button onClick={() => void handleBulkSkip()}>Confirm skip</Button>
        </div>
      </Modal>

      <MembershipFormModal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
      />
    </PageContainer>
  );
};
